import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Lightbulb, MapPin, Package, Radio, UserPlus, Users } from 'lucide-react';

import { VendorState } from '../../../redux/vendor/rootReducer';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { useVisitorTracking } from '../../../common/hooks/use-visitor-tracking.hook';
import { useAnalyticsApi } from '../../../common/hooks/use-analytics-api.hook';
import {
    getDateRangeForPeriod,
    getPreviousPeriodDateRange,
    processLeadsData,
} from '../../../modules/vendor/dashboard/dashboard-data';
import { InsightsPanel } from '../../../modules/vendor/insights/components/InsightsPanel';
import { SocialPostsCard } from '../../../modules/vendor/dashboard/SocialPostsCard';
import { CitiesMap } from '../../../modules/vendor/dashboard/components/cities-map.component';
import { StatCard } from '../../../modules/vendor/dashboard/components/stat-card.component';
import { LeadsCard, DashboardLead } from '../../../modules/vendor/dashboard/components/leads-card.component';
import { TopProductsCard } from '../../../modules/vendor/dashboard/components/top-products-card.component';
import { OnlineVisitorsCard } from '../../../modules/vendor/dashboard/components/online-visitors-card.component';
import { WidgetCanvas, WidgetDefinition } from '../../../modules/vendor/dashboard/widgets/WidgetCanvas';
import { WidgetPlacement } from '../../../modules/vendor/dashboard/widgets/widget-layout';

/**
 * The vendor's own widgets on the overview — the part they arrange.
 *
 * Mounted only when they have placed a widget or are picking one, so a vendor
 * who never did pays for none of it: no analytics queries, no live-visitor
 * socket, no empty canvas. The catalogue is the one dashboard.page.tsx used to
 * declare, unchanged, so every saved arrangement still renders.
 */
export const OverviewWidgets: React.FC<{
    layout: WidgetPlacement[];
    onChange: (next: WidgetPlacement[]) => void;
    picking: boolean;
    onPickingChange: (open: boolean) => void;
}> = ({ layout, onChange, picking, onPickingChange }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuthContext();
    const analyticsApi = useAnalyticsApi();

    // Only open the live-visitor socket when the widget that shows it is on the page.
    const onlinePlaced = layout.some((p) => p.id === 'online');
    const onlineVisitors = useVisitorTracking(onlinePlaced ? user?.vendor?.alias : undefined);

    /* Widget data. Fetched once here rather than per widget, so rearranging
       the canvas never refetches and two widgets cannot disagree. */
    const [overview, setOverview] = useState<any>(null);
    const [leads, setLeads] = useState<DashboardLead[]>([]);
    const [products, setProducts] = useState<any[]>([]);
    const [cities, setCities] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                // The same questions the Visualizer overview asks, through the same
                // helpers, so the two screens cannot disagree about the period.
                const filters = {
                    dateRange: getDateRangeForPeriod('month'),
                    comparisonPeriod: getPreviousPeriodDateRange('month'),
                };

                const [stats, top, leadRows] = await Promise.all([
                    analyticsApi.getDashboardData(filters).catch(() => null),
                    analyticsApi.getProductAnalytics(filters, 5).catch(() => null),
                    analyticsApi.queryAnalytics({ statType: 'PRODUCT_RESULT_DOWNLOADED_COUNT' }).catch(() => null),
                ]);
                if (!alive) return;

                setOverview((stats as any)?.overview ?? null);
                setCities((stats as any)?.cities ?? (stats as any)?.overview?.cities ?? []);
                setProducts((top as any)?.data?.products || (top as any)?.products || []);
                setLeads(processLeadsData(leadRows).slice(0, 5));
            } catch {
                /* a widget with no data shows its own empty state */
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Every widget under "Visualizer" reports on what visitors did. A vendor who has
     * switched measurement off has none of that, so the whole category is dropped rather
     * than offered as six cards that would each render a zero. Filtering the category
     * rather than naming the six keeps a widget added later from quietly reappearing.
     */
    const { analyticsEnabled } = useSelector((state: VendorState) => state.settings.theme);
    const measures = analyticsEnabled !== false;

    const definitions: WidgetDefinition[] = useMemo(
        () => ([
            {
                id: 'visits',
                category: 'visualizer',
                defaultSize: { w: 3, h: 4 },
                minSize: { w: 2, h: 3 },
                icon: <Users />,
                description: t('PAGES.DASHBOARD.WIDGETS.DESC.VISITS'),
                title: t('ANALYTICS.MODERN_ANALYTICS.TOTAL_VISITS'),
                render: () => (
                    <StatCard
                        title={t('ANALYTICS.MODERN_ANALYTICS.TOTAL_VISITS')}
                        value={overview?.totalVisits?.value ?? 0}
                        percentageChange={overview?.totalVisits?.change ?? 0}
                        comparisonLabel={t('PAGES.DASHBOARD.VS_PRIOR_30_DAYS')}
                        icon={<Users />}
                        loading={loading}
                    />
                ),
            },
            {
                id: 'online',
                category: 'visualizer',
                defaultSize: { w: 3, h: 5 },
                minSize: { w: 2, h: 3 },
                icon: <Radio />,
                description: t('PAGES.DASHBOARD.WIDGETS.DESC.ONLINE'),
                title: t('PAGES.DASHBOARD.ONLINE_NOW'),
                render: () => <OnlineVisitorsCard visitors={onlineVisitors} />,
            },
            {
                id: 'leads',
                category: 'visualizer',
                defaultSize: { w: 6, h: 8 },
                minSize: { w: 3, h: 4 },
                icon: <UserPlus />,
                description: t('PAGES.DASHBOARD.WIDGETS.DESC.LEADS'),
                title: t('SIDEBAR.ENGAGEMENT.LEADS'),
                render: () => (
                    <LeadsCard leads={leads} loading={loading} onViewAll={() => navigate('/visualizer/engagement/leads')} />
                ),
            },
            {
                id: 'products',
                category: 'visualizer',
                defaultSize: { w: 6, h: 8 },
                minSize: { w: 3, h: 4 },
                icon: <Package />,
                description: t('PAGES.DASHBOARD.WIDGETS.DESC.PRODUCTS'),
                title: t('PAGES.DASHBOARD.TOP_PRODUCTS'),
                render: () => <TopProductsCard products={products} loading={loading} onViewAll={() => navigate('/visualizer/engagement/analytics?tab=products')} />,
            },
            {
                id: 'socialPosts',
                category: 'marketing',
                // Tall by default: it shows the scheduler's own post preview, and one of
                // those needs room for a square image plus its caption.
                defaultSize: { w: 4, h: 16 },
                minSize: { w: 3, h: 7 },
                icon: <CalendarClock />,
                description: t('PAGES.DASHBOARD.WIDGETS.DESC.SOCIAL_POSTS'),
                title: t('PAGES.DASHBOARD.SOCIAL_POSTS'),
                render: () => <SocialPostsCard />,
            },
            {
                id: 'insights',
                category: 'visualizer',
                defaultSize: { w: 12, h: 6 },
                minSize: { w: 4, h: 5 },
                icon: <Lightbulb />,
                description: t('PAGES.DASHBOARD.WIDGETS.DESC.INSIGHTS'),
                title: t('INSIGHTS.TITLE'),
                render: () => <InsightsPanel />,
            },
            {
                id: 'map',
                category: 'visualizer',
                defaultSize: { w: 6, h: 10 },
                minSize: { w: 4, h: 6 },
                icon: <MapPin />,
                description: t('PAGES.DASHBOARD.WIDGETS.DESC.MAP'),
                title: t('PAGES.DASHBOARD.VISITORS_BY_CITY'),
                render: () => (
                    <div className="overflow-hidden rounded-xl border bg-card">
                        <CitiesMap data={cities} loading={loading} />
                    </div>
                ),
            },
        ] as WidgetDefinition[]).filter((d) => measures || d.category !== 'visualizer'),
        [t, overview, leads, products, cities, loading, navigate, onlineVisitors, measures],
    );

    return (
        <WidgetCanvas
            layout={layout}
            definitions={definitions}
            onChange={onChange}
            picking={picking}
            onPickingChange={onPickingChange}
        />
    );
};
